import { makeStyles, Theme } from '@material-ui/core';
import { createStyles } from '@material-ui/styles';

import * as Colors from 'utils/color-styles';

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawer: {
      flexShrink: 0,
    },
    drawerPaper: {
      width: 600,
      borderRadius: `${theme.spacing(1)}px 0 0 ${theme.spacing(1)}px`,
      border: `1px solid ${theme.palette.divider}`,
      position: 'fixed',
      top: 80,
      height: 'calc(100vh - 80px)',
      overflow: 'visible',
      zIndex: 2000,
      right: 0,
      boxShadow: theme.shadows[1],
    },
    content: {
      padding: `0 ${theme.spacing(1.5)}px ${theme.spacing(1.5)}px ${theme.spacing(1.5)}px`,
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 180px)', // Account for drawer header and padding
      overflow: 'hidden',
    },
    pill: {
      position: 'fixed',
      top: 100,
      right: 0,
      backgroundColor: Colors.purple,
      color: theme.palette.common.white,
      borderRadius: `${theme.spacing(3)}px 0 0 ${theme.spacing(3)}px`,
      padding: theme.spacing(1, 2),
      cursor: 'pointer',
      zIndex: theme.zIndex.drawer + 1,
      display: 'flex',
      alignItems: 'center',
      boxShadow: theme.shadows[2],
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shorter,
      }),
      transform: 'translateX(0)',
      '&:hover': {
        transform: 'translateX(-5px)',
      },
    },
    logoIcon: {
      width: 60,
      height: 30,
    },
    logoWhite: {
      filter: 'brightness(0) invert(1)',
    },
    logoPurple: {
      color: Colors.purple,
    },
    presetContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      overflowY: 'auto',
      flex: 1,
      paddingTop: theme.spacing(0.5), // Add small padding to prevent border cutoff
      paddingRight: theme.spacing(1), // Space for scrollbar
      // Custom scrollbar styling
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: theme.palette.grey[200],
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: theme.palette.grey[400],
        borderRadius: '4px',
        '&:hover': {
          background: theme.palette.grey[500],
        },
      },
    },
    presetButton: {
      background: 'none',
      border: 'none',
      padding: 0,
      margin: 0,
      cursor: 'pointer',
      textAlign: 'left',
      width: '100%',
      '&:focus': {
        outline: `2px solid ${Colors.purple}`,
        borderRadius: theme.spacing(2),
      },
    },
    betaPill: {
      backgroundColor: 'rgba(103, 58, 183, 0.1)',
      color: Colors.purple,
      border: `2px solid ${Colors.purple}`,
      borderRadius: theme.spacing(2),
      padding: theme.spacing(0.25, 1.25),
      display: 'flex',
      alignItems: 'center',
    },
    betaText: {
      fontWeight: 'bold',
      fontSize: '10px',
      letterSpacing: '1px',
    },
    // Chat-related styles
    chatContainer: {
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 150px)',
      padding: theme.spacing(2),
      position: 'relative',
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      paddingRight: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    messageWrapper: {
      display: 'flex',
      width: '100%',
      margin: theme.spacing(1, 0),
    },
    userMessage: {
      justifyContent: 'flex-end',
    },
    botMessage: {
      justifyContent: 'flex-start',
    },
    messageBubble: {
      borderRadius: theme.spacing(2),
      maxWidth: '80%',
      boxShadow: 'none',
      wordBreak: 'break-word',
      overflow: 'hidden',
      padding: theme.spacing(1, 2),
      display: 'inline-block',
      width: 'auto',
    },
    userMessageBubble: {
      backgroundColor: '#F6F4FB',
      color: theme.palette.text.primary,
      border: 'none',
      borderRadius: `${theme.spacing(2)}px ${theme.spacing(2)}px 0 ${theme.spacing(2)}px`,
      marginLeft: 'auto',
    },
    botMessageBubble: {
      backgroundColor: '#F4F4F6',
      color: theme.palette.text.primary,
      border: 'none',
      borderRadius: `${theme.spacing(2)}px ${theme.spacing(2)}px ${theme.spacing(2)}px 0`,
    },

    choicesContainer: {
      marginTop: 'auto',
      padding: theme.spacing(2),
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    // Inline choice selector styles
    inlineChoiceWrapper: {
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      margin: '0 auto',
      maxWidth: '90%',
      position: 'relative',
      // paddingTop: theme.spacing(3),
    },
    inlineChoiceContainer: {
      padding: `${theme.spacing(1)}px 0`,
      maxWidth: '100%',
      position: 'relative',
    },
    // Tag cloud related styles
    tagCloudContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(1),
      margin: `${theme.spacing(1)}px 0 ${theme.spacing(2)}px`,
      width: '100%',
    },
    tagItem: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: theme.spacing(0.75, 1.5),
      borderRadius: theme.spacing(2.5),
      backgroundColor: '#F6F4FB',
      cursor: 'pointer',
      transition: theme.transitions.create(['transform', 'box-shadow'], {
        duration: theme.transitions.duration.shorter,
      }),
      margin: theme.spacing(0.5),
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[1],
      },
    },

    searchIcon: {
      display: 'inline-block',
      animation: '$circularMotion 2s ease-in-out infinite',
      fontSize: '1.5rem',
      marginRight: theme.spacing(1),
      transform: 'translateX(-8px)',
    },
    '@keyframes circularMotion': {
      '0%': {
        transform: 'translate(5px, 0px)',
      },
      '25%': {
        transform: 'translate(0px, -5px)',
      },
      '50%': {
        transform: 'translate(-5px, 0px)',
      },
      '75%': {
        transform: 'translate(0px, 5px)',
      },
      '100%': {
        transform: 'translate(5px, 0px)',
      },
    },
  })
);
